import React from 'react';
import { Button, Dropdown, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../../../utils/currency';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { summarizeLoadSupplierOrders, type LoadGroup, type LoadQuantity, type LoadRecipient, type LoadSupplierOrderSummary } from '../transportLoadTable';
import type { TrailerTone } from '../transportTrailerLoad';
import './TransportLinkedOrdersTable.css';
import './TransportTrailerView.css';

interface Row {
  key: string;
  group: LoadGroup;
  recipient: LoadRecipient;
  stop: number | null;
}

/** With two or more ZD the rows sit under one header per ZD (truck order, then „Bez ZD”). */
interface HeaderRow {
  key: string;
  header: true;
  supplierOrder: LoadSupplierOrderSummary | null;
  /** Why the „no ZD” group has no freight: one reason shared by all its rows, else 'mixed'. */
  missing: 'unpaired' | 'exceeds' | 'noLine' | 'mixed' | null;
}

const headerMissing = (members: Row[]): HeaderRow['missing'] => {
  const reasons = new Set(members.map((row) => row.group.missing ?? 'noLine'));
  // Any row only lacking its pair names the fix for the group: „z pozycji ZD”.
  if (reasons.has('unpaired')) return 'unpaired';
  return reasons.size === 1 ? Array.from(reasons)[0] : 'mixed';
};

const isHeader = (row: Row | HeaderRow): row is HeaderRow => 'header' in row;

interface Props {
  groups: LoadGroup[];
  /** Same colour and stop number per customer order as on the trailer. */
  toneByOrderId?: Map<string, TrailerTone>;
  stopByOrderId?: Map<string, number>;
  showCosts: boolean;
  onOpenOrder: (orderId: string) => void;
  onOpenSupplierOrder: (supplierOrderId: string) => void;
  onUnlink?: (orderId: string) => void;
}

/**
 * „Ładunek” of an INBOUND truck, one row per recipient in the trailer's stop
 * order (ZK by stop, the stock part last — „Nadwyżka — na magazyn” beyond customers): who and what (with the ZD and its PZ
 * receipt) | quantity and weight | freight and freight per unit. Values are
 * stacked so the table fits a narrow card without horizontal scrolling
 * (owner 2026-10-06). Rows come from `transportLoadTable.ts`; money only
 * with cost access.
 */
export const TransportLoadTable: React.FC<Props> = ({
  groups, toneByOrderId, stopByOrderId, showCosts, onOpenOrder, onOpenSupplierOrder, onUnlink,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const qty = (quantity: LoadQuantity | null) => (quantity
    ? `${formatLocalizedQuantity(quantity.value, i18n.language)} ${formatUnit(quantity.unit, i18n.language)}`
    : '—');
  const money = (value: number | null) => (value === null ? '—' : formatMoneyPl(value, 'PLN'));

  const rows: Row[] = groups
    .flatMap((group) => group.recipients.map((recipient) => ({
      key: recipient.key,
      group,
      recipient,
      stop: recipient.orderId ? stopByOrderId?.get(recipient.orderId) ?? null : null,
    })))
    // Trailer order: stops 1…n, the warehouse last (stable within a stop).
    .map((row, index) => ({ row, index }))
    .sort((a, b) => (a.row.stop ?? Number.MAX_SAFE_INTEGER) - (b.row.stop ?? Number.MAX_SAFE_INTEGER) || a.index - b.index)
    .map(({ row }) => row);

  const supplierOrders = summarizeLoadSupplierOrders(groups);
  const byZd = supplierOrders.length > 1;
  const displayRows: Array<Row | HeaderRow> = byZd
    ? [...supplierOrders.map((supplierOrder) => supplierOrder as LoadSupplierOrderSummary | null), null].flatMap((supplierOrder) => {
      const members = rows.filter((row) => (row.group.supplierOrder?.id ?? null) === (supplierOrder?.id ?? null));
      return members.length === 0 ? [] : [{
        key: `zd:${supplierOrder?.id ?? 'none'}`, header: true as const, supplierOrder,
        missing: supplierOrder ? null : headerMissing(members),
      }, ...members];
    })
    : rows;

  const toneOf = ({ recipient, group }: Row) => (recipient.kind === 'stock'
    ? 'stock'
    : !group.supplierOrder ? 'uncovered' : toneByOrderId?.get(recipient.orderId || ''));

  const columns: ColumnsType<Row> = [
    {
      key: 'actions',
      width: 48,
      align: 'center',
      render: (_, { recipient }) => (recipient.kind === 'order' && recipient.orderId ? (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'open', icon: <ExportOutlined />, label: t('transport:linkedOrdersTable.openOrder') },
              ...(onUnlink ? [{ key: 'unlink', danger: true, label: t('transport:orderTransports.unlink') }] : []),
            ],
            onClick: ({ key }) => (key === 'unlink' ? onUnlink?.(recipient.orderId as string) : onOpenOrder(recipient.orderId as string)),
          }}
        >
          <Button size="small" icon={<MoreOutlined />} title={t('common:actions')} />
        </Dropdown>
      ) : null),
    },
    {
      key: 'who',
      title: t('transport:loadTable.recipientGoods'),
      render: (_, row) => {
        const { group, recipient, stop } = row;
        const tone = toneOf(row);
        return (
          <div className={`transport-linked-orders__identity${tone ? ` transport-load-tone transport-tone--${tone}` : ''}`}>
            {recipient.kind === 'stock' ? (
              <span className="transport-linked-orders__product">{t(recipient.surplus ? 'transport:loadTable.surplusToStock' : 'transport:loadTable.toStock')}</span>
            ) : (
              <span className="transport-load-who">
                {stop ? `${stop}. ` : ''}
                <button type="button" className="transport-linked-orders__order-link" onClick={() => recipient.orderId && onOpenOrder(recipient.orderId)}>
                  {recipient.orderNumber || '—'}
                </button>
                {recipient.customerName ? <span className="transport-load-who__customer"> · {recipient.customerName}</span> : null}
                {group.supplierOrder && !recipient.paired ? (
                  <Tooltip title={t('transport:loadTable.toAssignHint')}>
                    <Tag color="warning" bordered={false} className="transport-linked-orders__tag" data-testid="transport-load-to-assign">
                      {t('transport:loadTable.toAssign')}
                    </Tag>
                  </Tooltip>
                ) : null}
              </span>
            )}
            {recipient.overAssigned ? (
              <Tag color="warning" bordered={false} className="transport-linked-orders__tag" data-testid="transport-load-over-assigned">
                {t('transport:loadTable.overAssigned', { quantity: qty(recipient.overAssigned) })}
              </Tag>
            ) : null}
            {recipient.deliveryAddress ? (
              <span className="transport-linked-orders__meta">{t('transport:orderRoutePoints.deliveryAddress')}: {recipient.deliveryAddress}</span>
            ) : null}
            <span className="transport-load-goods">
              {group.productName}
              {group.dimensions ? <span className="transport-linked-orders__meta"> · {group.dimensions}</span> : null}
            </span>
            {group.supplierOrder ? (
              <span className="transport-linked-orders__meta">
                <button type="button" className="transport-linked-orders__order-link transport-linked-orders__order-link--small" onClick={() => onOpenSupplierOrder(group.supplierOrder!.id)}>
                  {group.supplierOrder.label}
                </button>
                {group.carried ? ` · ${t('transport:loadTable.received', {
                  received: group.received === null ? '—' : formatLocalizedQuantity(group.received, i18n.language),
                  carried: qty(group.carried),
                })}` : null}
              </span>
            ) : (
              <Tag color="warning" bordered={false} className="transport-linked-orders__tag" data-testid={`transport-load-missing-${group.missing ?? 'noLine'}`}>
                {t(group.missing === 'unpaired' ? 'transport:loadTable.unpaired'
                  : group.missing === 'exceeds' ? 'transport:loadTable.exceeds' : 'transport:loadTable.noSupplierOrder')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      key: 'quantity',
      title: t('transport:loadTable.quantity'),
      align: 'right',
      width: 104,
      render: (_, { recipient }) => (
        <div className="transport-load-numbers">
          <strong>{qty(recipient.quantity)}</strong>
          <span className={recipient.weightKg === null ? 'transport-load-numbers__warn' : undefined}>
            {recipient.weightKg === null ? t('transport:trailer.noWeight') : `${formatLocalizedNumber(recipient.weightKg, 0, i18n.language)} kg`}
          </span>
          {recipient.delivered ? (
            <span>{t('transport:delivered')}: {formatLocalizedQuantity(recipient.delivered.value, i18n.language)} {formatUnit(recipient.delivered.unit, i18n.language)}</span>
          ) : null}
          {recipient.planned ? (
            <span data-testid="transport-load-planned">{t('transport:loadTable.planned', {
              quantity: `${formatLocalizedQuantity(recipient.planned.value, i18n.language)} ${formatUnit(recipient.planned.unit, i18n.language)}`,
            })}</span>
          ) : null}
        </div>
      ),
    },
    ...(showCosts ? [{
      key: 'freight',
      title: t('transport:loadTable.freight'),
      align: 'right' as const,
      width: 136,
      render: (_: unknown, { group, recipient }: Row) => (
        <div className="transport-load-numbers">
          <strong className={!group.supplierOrder ? 'transport-load-numbers__warn' : undefined}>
            {!group.supplierOrder ? formatMoneyPl(0, 'PLN') : money(recipient.freightPLN)}
          </strong>
          {group.freightPerUnits.length > 0
            ? group.freightPerUnits.map((perUnit) => (
              <span key={perUnit.unit}>{money(perUnit.value)} / {formatUnit(perUnit.unit, i18n.language)}</span>
            ))
            : <span>—</span>}
        </div>
      ),
    }] : []),
  ];

  const headerCell = (row: HeaderRow) => (
    <div className="transport-load-zd-header" data-testid="transport-load-zd-header">
      {row.supplierOrder ? (
        <>
          <span>
            {row.supplierOrder.label}
            {row.supplierOrder.supplierName ? ` · ${row.supplierOrder.supplierName}` : ''}
          </span>
          {showCosts && row.supplierOrder.freightPLN !== null ? (
            <strong className="transport-load-zd-header__freight">{money(row.supplierOrder.freightPLN)}</strong>
          ) : null}
        </>
      ) : <span>{t(row.missing === 'unpaired' ? 'transport:loadTable.unpairedHeader'
        : row.missing === 'exceeds' ? 'transport:loadTable.exceedsHeader'
          : row.missing === 'mixed' ? 'transport:loadTable.noFreightHeader' : 'transport:loadTable.noZdHeader')}</span>}
    </div>
  );
  const tableColumns: ColumnsType<Row | HeaderRow> = columns.map((column, index) => ({
    ...column,
    onCell: (row: Row | HeaderRow) => (isHeader(row) ? { colSpan: index === 0 ? columns.length : 0 } : {}),
    render: (value: unknown, row: Row | HeaderRow, rowIndex: number) => (isHeader(row)
      ? (index === 0 ? headerCell(row) : null)
      : (column as { render?: (v: unknown, r: Row, i: number) => React.ReactNode }).render?.(value, row, rowIndex)),
  })) as ColumnsType<Row | HeaderRow>;

  return (
    <Table<Row | HeaderRow>
      className="transport-linked-orders transport-load-table"
      data-testid="transport-load-table"
      rowKey="key"
      size="small"
      bordered
      pagination={false}
      columns={tableColumns}
      dataSource={displayRows}
      rowClassName={(row) => (isHeader(row) ? 'transport-load-zd-row' : '')}
      tableLayout="fixed"
    />
  );
};
